import { useMemo } from 'react';
import { useList } from '@refinedev/core';
import type { OrganizationPublisherOut } from '@/api/generated/schemas';
import {
  resolveOrganizationPublisherLinks,
  upcomingPublisherIds,
} from '@/components/tenancy/organizationPublisherLinks';
import { PUBLISHER_TREE_LIMIT } from '@/components/user_catalog/publishers/publisherTree';

const RESOURCE = 'tenancy.organization_publishers';

/**
 * An organization's publisher links with their status on today's timeline.
 *
 * Two requests: the organization's own links, then every link of those
 * publishers — a move to another organization is a link there, and only it
 * tells a past link apart from one still in force. Both lists are invalidated
 * by refine on any create/delete of the resource, so callers need no refetch.
 */
export const useOrganizationPublisherLinks = (
  organizationId: number | undefined,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  const { result: orgResult, query: orgQuery } =
    useList<OrganizationPublisherOut>({
      resource: RESOURCE,
      filters: [
        { field: 'organization_id', operator: 'eq', value: organizationId },
      ],
      sorters: [{ field: 'valid_from', order: 'desc' }],
      pagination: { pageSize: PUBLISHER_TREE_LIMIT },
      queryOptions: { enabled: enabled && organizationId != null },
    });
  const orgLinks = orgResult?.data;

  const publisherIds = useMemo(
    () => [...new Set((orgLinks ?? []).map((link) => link.publisher_id))],
    [orgLinks]
  );
  const { result: allResult, query: allQuery } =
    useList<OrganizationPublisherOut>({
      resource: RESOURCE,
      filters: [{ field: 'publisher_id', operator: 'in', value: publisherIds }],
      pagination: { pageSize: PUBLISHER_TREE_LIMIT },
      queryOptions: { enabled: enabled && publisherIds.length > 0 },
    });
  const allLinks = allResult?.data;

  const links = useMemo(
    () => resolveOrganizationPublisherLinks(orgLinks ?? [], allLinks ?? []),
    [orgLinks, allLinks]
  );

  return {
    links,
    /** Publishers in the organization now or joining it later. */
    publisherIds: useMemo(() => upcomingPublisherIds(links), [links]),
    isLoading:
      orgQuery.isLoading || (publisherIds.length > 0 && allQuery.isLoading),
  };
};
